<template>
  <el-alert
    v-if="failed"
    :title="$t('sampleProgress.timeline.error')"
    type="error"
    show-icon
    :closable="false"
  >
    <el-button
      type="text"
      @click="load"
    >
      {{ $t('sampleProgress.retry') }}
    </el-button>
  </el-alert>
  <el-skeleton
    v-else
    :loading="!events"
    :rows="3"
    animated
  >
    <template v-if="events">
      <el-empty
        v-if="!events.length"
        :description="$t('sampleProgress.timeline.empty')"
      />
      <template v-else>
        <el-timeline>
          <el-timeline-item
            v-for="event in events"
            :key="event.eventId"
            :timestamp="formatDate(event)"
            placement="top"
          >
            {{ event.title }}
            <el-tag
              v-if="event.attachmentCount"
              size="mini"
              type="info"
            >
              <i
                class="fas fa-paperclip"
                aria-hidden="true"
              />
              {{ $tc('sampleProgress.timeline.attachmentCount', event.attachmentCount) }}
            </el-tag>
          </el-timeline-item>
        </el-timeline>
        <el-row
          v-if="nextCursor"
          type="flex"
          justify="center"
        >
          <el-button
            :loading="loadingEarlier"
            icon="el-icon-arrow-down"
            size="small"
            @click="loadEarlier"
          >
            {{ $t('sampleProgress.timeline.loadEarlier') }}
          </el-button>
        </el-row>
      </template>
    </template>
  </el-skeleton>
</template>

<script>
import { sampleProgressApi } from '@/api';
import progressDate from '@/services/progressDate';

/**
 * The timeline events of one step, newest first, one page at a time. Earlier pages are appended
 * below on request, resuming from the cursor the backend handed back: an event sharing its
 * timestamp with the last one shown is neither repeated nor skipped.
 */
export default {
  name: 'ProgressTimeline',
  props: {
    sampleId: {
      type: Number,
      required: true,
    },
    stepId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      // Null until the first page arrives.
      events: null,
      // Where the earlier page resumes from; null once none is left.
      nextCursor: null,
      failed: false,
      loadingEarlier: false,
      // Bumped on every reload: an answer to an earlier one is ignored, even for the same step.
      generation: 0,
    };
  },
  watch: {
    stepId() {
      this.load();
    },
    '$i18n.locale': 'load',
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.generation += 1;
      const { generation, stepId } = this;
      this.events = null;
      this.nextCursor = null;
      this.failed = false;
      this.loadingEarlier = false;
      try {
        const { data } = await sampleProgressApi.findTimeline(this.sampleId, { stepId });
        if (generation !== this.generation) return;
        this.events = data.events;
        this.nextCursor = data.nextCursor;
      } catch (error) {
        if (generation !== this.generation) return;
        this.failed = true;
      }
    },
    // A failure keeps what is shown and the cursor, so the same button tries again.
    async loadEarlier() {
      const { generation, stepId, nextCursor: cursor } = this;
      this.loadingEarlier = true;
      try {
        const { data } = await sampleProgressApi.findTimeline(this.sampleId, { stepId, cursor });
        if (generation !== this.generation) return;
        this.events = [...this.events, ...data.events];
        this.nextCursor = data.nextCursor;
      } catch (error) {
        if (generation !== this.generation) return;
        this.$notify({
          showClose: true,
          type: 'error',
          message: this.$t('sampleProgress.timeline.loadEarlierError'),
        });
      } finally {
        if (generation === this.generation) this.loadingEarlier = false;
      }
    },
    formatDate({ occurredAt }) {
      return progressDate.dateTime(occurredAt);
    },
  },
};
</script>
